import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Keyboard, EffectCoverflow } from 'swiper/modules';
import { galleryService } from '../../services/galleryService';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';
import './PhotoGallery.css';

// Suppress ResizeObserver errors
const suppressResizeObserverErrors = () => {
    const resizeObserverErrorHandler = (e) => {
        if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
            e.stopImmediatePropagation();
            return true;
        }
    };
    
    window.addEventListener('error', resizeObserverErrorHandler);
    window.addEventListener('unhandledrejection', (e) => {
        if (e.reason?.message === 'ResizeObserver loop completed with undelivered notifications.') {
            e.preventDefault();
        }
    });
    
    return () => {
        window.removeEventListener('error', resizeObserverErrorHandler);
    };
};

// Lazy loading image component with Intersection Observer
const LazyImage = ({ src, alt, className, onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className={`lazy-image-wrapper ${className || ''}`} onClick={onClick}>
      {isInView && (
        <>
          {!isLoaded && <div className="image-skeleton shimmer" />}
          <img
            src={src}
            alt={alt}
            loading="lazy"
            onLoad={() => setIsLoaded(true)}
            style={{ opacity: isLoaded ? 1 : 0 }}
            className="lazy-image"
          />
        </>
      )}
    </div>
  );
};

const PhotoGallery = () => {
  const [activeTab, setActiveTab] = useState('photos');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState('All');
  const [mediaItems, setMediaItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [error, setError] = useState(null);
  const observerRef = useRef();
  const lastItemRef = useRef();
  const swiperRef = useRef(null);

  // Suppress ResizeObserver errors on mount
  useEffect(() => {
    const cleanup = suppressResizeObserverErrors();
    return cleanup;
  }, []);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Reset and fetch media when tab or category changes
  useEffect(() => {
    setMediaItems([]);
    setPage(0);
    setHasMore(true);
    fetchMedia(0, true);
  }, [activeTab, activeCategory]);

  // Setup infinite scroll observer
  useEffect(() => {
    if (loadingMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          fetchMedia(page + 1);
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (lastItemRef.current) {
      observer.observe(lastItemRef.current);
    }

    return () => observer.disconnect();
  }, [mediaItems, hasMore, loading, loadingMore, page]);

  const fetchCategories = async () => {
    try {
      const response = await galleryService.getCategories();
      if (response.success && response.categories) {
        setCategories([
          { name: 'All', count: response.categories.reduce((sum, cat) => sum + cat.count, 0) },
          ...response.categories
        ]);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchMedia = async (pageNum = 0, reset = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    setError(null);
    
    try {
      const type = activeTab === 'photos' ? 'image' : 'video';
      const category = activeCategory !== 'All' ? activeCategory : '';
      const limit = 24;
      const offset = pageNum * limit;
      
      const response = await galleryService.getMedia(type, category, limit, offset);
      
      if (response.success) {
        if (reset) {
          setMediaItems(response.media || []);
        } else {
          setMediaItems(prev => [...prev, ...(response.media || [])]);
        }
        setHasMore(response.hasMore || false);
        setPage(pageNum);
      } else {
        setError('Failed to load media');
      }
    } catch (err) {
      console.error('Error fetching media:', err);
      setError('Failed to load media. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/')) return `https://nlngraclub.com${url}`;
    return `https://nlngraclub.com/${url}`;
  };

  const handleMediaClick = async (media, index) => {
    setSelectedMedia(media);
    setSelectedMediaIndex(index);
    if (media.id) {
      await galleryService.incrementViews(media.id);
    }
  };

  const closeModal = () => {
    setSelectedMedia(null);
    setSelectedMediaIndex(0);
    // Clean up swiper instance
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.destroy(true, true);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedMedia) return;
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedMedia]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedMedia) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedMedia]);

  return (
    <div className="gallery-page">
      {/* Hero Section */}
      <div className="gallery-hero">
        <div className="hero-background"></div>
        <div className="container">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="hero-eyebrow">Memories & Moments</span>
            <h1 className="hero-title">Visual Journey</h1>
            <p className="hero-subtitle">Discover the story of our community through images and videos</p>
          </motion.div>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" fill="currentColor"></path>
            <path d="M0,0V15.81C13,21.25,27.93,25.67,44.24,28.45c69.76,11.6,136.47,7.22,206.42-5.49C312.27,5.42,383,11.43,452.42,25.36c69.07,13.88,135.53,35.66,206.8,44.41,73.71,9.05,145.93,3.28,218.11-12.59,71.19-15.65,138.79-42.44,210.22-65.18C1084.24,0,1142.07,0,1200,0Z" opacity=".5" fill="currentColor"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c119-21.25,236.44-48.9,354.38-69.62C972.9,0,1086.55,0,1200,0Z" fill="currentColor"></path>
          </svg>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="gallery-tabs">
        <div className="container">
          <div className="tabs-wrapper">
            <button 
              className={`tab-btn ${activeTab === 'photos' ? 'active' : ''}`}
              onClick={() => setActiveTab('photos')}
            >
              <svg className="tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="2" width="20" height="20" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="2.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              <span>Photos</span>
            </button>
            <button 
              className={`tab-btn ${activeTab === 'videos' ? 'active' : ''}`}
              onClick={() => setActiveTab('videos')}
            >
              <svg className="tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="23 7 16 12 23 17 23 7"></polygon>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
              </svg>
              <span>Videos</span>
            </button>
          </div>
        </div>
      </div>

      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="container">
          {/* Category Filters */}
          {categories.length > 0 && (
            <div className="gallery-filters">
              <div className="filters-scroll">
                {categories.map(cat => (
                  <motion.button
                    key={cat.name}
                    className={`filter-btn ${activeCategory === cat.name ? 'active' : ''}`}
                    onClick={() => setActiveCategory(cat.name)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>{cat.name}</span>
                    {cat.count > 0 && <span className="filter-count">{cat.count}</span>}
                  </motion.button>
                ))}
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading amazing moments...</p>
            </div>
          )}

          {/* Error State */}
          {!loading && error && (
            <div className="error-state">
              <svg className="error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <p>{error}</p>
              <button onClick={() => fetchMedia(0, true)} className="retry-btn">
                Try Again
              </button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && mediaItems.length === 0 && (
            <div className="empty-state">
              <svg className="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <rect x="2" y="2" width="20" height="20" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="2.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              <h3>No {activeTab} found</h3>
              <p>Check back later for new content in this category</p>
            </div>
          )}

          {/* Media Grid */}
          {!loading && !error && mediaItems.length > 0 && (
            <div className="gallery-grid">
              {mediaItems.map((media, index) => (
                <motion.div
                  key={`${media.id}-${index}`}
                  className="gallery-item"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.02, 0.3) }}
                  whileHover={{ y: -4 }}
                  onClick={() => handleMediaClick(media, index)}
                  ref={index === mediaItems.length - 1 ? lastItemRef : null}
                >
                  <div className="gallery-item-inner">
                    <div className="gallery-image-wrapper">
                      {media.media_type === 'image' ? (
                        <LazyImage
                          src={getFullUrl(media.thumbnail_url || media.file_url)}
                          alt={media.title}
                          className="gallery-image"
                        />
                      ) : (
                        <div className="video-thumbnail">
                          <LazyImage
                            src={getFullUrl(media.thumbnail_url || 'https://nlngraclub.com/images/video-placeholder.jpg')}
                            alt={media.title}
                            className="video-thumb-image"
                          />
                          <div className="play-button">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <polygon points="5 3 19 12 5 21 5 3"></polygon>
                            </svg>
                          </div>
                        </div>
                      )}
                      
                      {/* Overlay with title */}
                      <div className="gallery-overlay">
                        <div className="overlay-content">
                          <h3>{media.title}</h3>
                          {media.category && <span className="overlay-category">{media.category}</span>}
                          <div className="overlay-stats">
                            <span>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="2"/>
                                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/>
                              </svg>
                              {media.views || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {media.featured === 1 && (
                        <div className="featured-badge">
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                          </svg>
                          <span>Featured</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Loading More Indicator */}
          {loadingMore && (
            <div className="loading-more">
              <div className="loading-spinner small"></div>
              <span>Loading more...</span>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedMedia && (
          <motion.div 
            className="gallery-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div 
              className="gallery-modal-content"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="modal-close-btn" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>

              <div className="modal-swiper-container">
                <Swiper
                  ref={swiperRef}
                  modules={[Navigation, Pagination, Keyboard, EffectCoverflow]}
                  initialSlide={selectedMediaIndex}
                  navigation
                  pagination={{ clickable: true, dynamicBullets: true }}
                  keyboard={{ enabled: true }}
                  effect="coverflow"
                  coverflowEffect={{
                    rotate: 0,
                    stretch: 50,
                    depth: 150,
                    modifier: 1,
                    slideShadows: false,
                  }}
                  grabCursor={true}
                  centeredSlides={true}
                  slidesPerView={1}
                  spaceBetween={20}
                  loop={mediaItems.length > 1}
                  className="gallery-swiper"
                  onInit={(swiper) => {
                    // Small delay to prevent ResizeObserver issues
                    setTimeout(() => {
                      swiper.update();
                    }, 100);
                  }}
                >
                  {mediaItems.map((media) => (
                    <SwiperSlide key={media.id}>
                      <div className="swiper-slide-content">
                        {media.media_type === 'image' ? (
                          <img 
                            src={getFullUrl(media.file_url)} 
                            alt={media.title}
                            loading="eager"
                          />
                        ) : (
                          <video 
                            src={getFullUrl(media.file_url)} 
                            controls 
                            autoPlay
                            className="modal-video"
                            playsInline
                          >
                            Your browser does not support the video tag.
                          </video>
                        )}
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>

              <div className="modal-info">
                <div className="modal-header-info">
                  <h3>{selectedMedia.title}</h3>
                  {selectedMedia.category && (
                    <span className="modal-category">{selectedMedia.category}</span>
                  )}
                </div>
                {selectedMedia.description && (
                  <p className="modal-description">{selectedMedia.description}</p>
                )}
                <div className="modal-meta">
                  <div className="modal-date">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    {new Date(selectedMedia.created_at).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                  <div className="modal-views">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="2"/>
                      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/>
                    </svg>
                    {selectedMedia.views || 0} views
                  </div>
                </div>
                <div className="modal-counter">
                  <span>{selectedMediaIndex + 1} / {mediaItems.length}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PhotoGallery;